import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useState, useEffect, Component } from "react";
import MobileLayout from "./components/MobileLayout";
import { TermsAcceptanceModal } from "./components/TermsAcceptanceModal";
import { PendingSchedulesModal, usePendingSchedules } from "./components/PendingSchedulesModal";
import { useSilentLocationTracker } from "./hooks/useSilentLocationTracker";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import PeoplePage from "./pages/PeoplePage";
import MinistriesPage from "./pages/MinistriesPage";
import FunctionsPage from "./pages/FunctionsPage";
import SongsPage from "./pages/SongsPage";
import EventsPage from "./pages/EventsPage";
import SchedulesPage from "./pages/SchedulesPage";
import FinancePage from "./pages/FinancePage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import NewsPage from "./pages/NewsPage";
import NewsConfigPage from "./pages/NewsConfigPage";
import AgendaConfigPage from "./pages/AgendaConfigPage";
import PublicRegistrationPage from "./pages/PublicRegistrationPage";
import PublicFormConfigPage from "./pages/PublicFormConfigPage";
import DevotionalPage from "./pages/DevotionalPage";
import DevotionalConfigPage from "./pages/DevotionalConfigPage";
import ScheduleConfirmPage from "./pages/ScheduleConfirmPage";
import CreatePasswordPage from "./pages/CreatePasswordPage";
import MyProfilePage from "./pages/MyProfilePage";
import SocialPage from "./pages/SocialPage";
import ProjectEnrollPage from "./pages/ProjectEnrollPage";
import PublicEnrollPage from "./pages/PublicEnrollPage";
import WhatsAppConfigPage from "./pages/WhatsAppConfigPage";
import WhatsAppPage from "./pages/WhatsAppPage";
import InstagramGuidePage from "./pages/InstagramGuidePage";
import PublicSongsPage from "./pages/PublicSongsPage";
import TrilhoReinoPage from "./pages/TrilhoReinoPage";
import WiFiPortalPage from "./pages/WiFiPortalPage";
import FrequenciaPage from "./pages/FrequenciaPage";
import FinanceiroInscricoesPage from "./pages/FinanceiroInscricoesPage";
import ConnectionsPage from "./pages/ConnectionsPage";
import "./App.css";

// Short WiFi redirect component - For Omada Controller Portal
// This component handles the redirect from Omada's captive portal
// URL format from Omada: /wifi-portal or /w (short) with optional MAC params
function WiFiRedirect() {
  const [redirectUrl, setRedirectUrl] = useState('');
  
  useEffect(() => {
    // Get URL params from Omada
    const urlParams = new URLSearchParams(window.location.search);
    const clientMac = urlParams.get('clientMac') || urlParams.get('client_mac') || '';
    const clientIp = urlParams.get('clientIP') || urlParams.get('client_ip') || '';
    const apMac = urlParams.get('apMac') || urlParams.get('ap_mac') || '';
    const ssidName = urlParams.get('ssidName') || urlParams.get('ssid') || '';
    
    // Build redirect URL to News with WiFi params
    const params = new URLSearchParams();
    params.set('wifi', 'true');
    if (clientMac) params.set('clientMac', clientMac);
    if (clientIp) params.set('clientIp', clientIp);
    if (apMac) params.set('apMac', apMac);
    if (ssidName) params.set('ssidName', ssidName);
    
    const url = `/news?${params.toString()}`;
    setRedirectUrl(url);
    
    // Try immediate redirect
    setTimeout(() => {
      window.location.replace(url);
    }, 100);
  }, []);
  
  // Fallback UI with manual link for captive portal browsers
  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Wi-Fi Life Canedo</h1>
        <p className="text-gray-600 mb-6">Conectando você à nossa rede...</p>
        
        <div className="animate-pulse flex justify-center mb-6">
          <div className="w-2 h-2 bg-orange-500 rounded-full mx-1"></div>
          <div className="w-2 h-2 bg-orange-500 rounded-full mx-1 animation-delay-200"></div>
          <div className="w-2 h-2 bg-orange-500 rounded-full mx-1 animation-delay-400"></div>
        </div>
        
        {redirectUrl && (
          <a 
            href={redirectUrl}
            className="inline-block w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Acessar Portal
          </a>
        )}
        
        <p className="text-xs text-gray-400 mt-4">
          Se não redirecionar automaticamente, toque no botão acima
        </p>
      </div>
    </div>
  );
}

// Workaround for React removeChild error caused by browser extensions or auto-translate
// This error happens when the DOM is modified externally (e.g., Google Translate, ad blockers)
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function(child) {
    if (child.parentNode !== this) {
      if (console) {
        console.warn('Cannot remove a child from a different parent', child, this);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) {
        console.warn('Cannot insert before a reference node from a different parent', referenceNode, this);
      }
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

// Error Boundary for catching render errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Ignore removeChild errors - they're usually from browser extensions
    if (error?.message?.includes('removeChild') || error?.message?.includes('insertBefore')) {
      console.warn('DOM manipulation error (likely from browser extension):', error);
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Ignore removeChild errors
    if (error?.message?.includes('removeChild') || error?.message?.includes('insertBefore')) {
      return;
    }
    console.error("App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4 text-center">
          <div className="text-red-500 text-xl">Erro ao carregar a aplicação</div>
          <p className="text-gray-600">Por favor, tente recarregar a página</p>
          <button 
            onClick={() => {
              localStorage.clear();
              window.location.reload();
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Recarregar
          </button>
          <p className="text-xs text-gray-400 mt-4">
            {this.state.error?.toString()}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, needsTermsAcceptance, acceptTerms } = useAuth();
  const [showTimeout, setShowTimeout] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingChecked, setPendingChecked] = useState(false);
  
  // Check for pending schedules
  const { hasPending, checked: pendingLoaded } = usePendingSchedules();

  // Silent location tracking for automatic event attendance
  useSilentLocationTracker({ user, enabled: true });

  // Show loading timeout message after 10 seconds
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowTimeout(true), 10000);
      return () => clearTimeout(timer);
    }
  }, [loading]);
  
  // Show pending schedules modal after login (only once per session)
  useEffect(() => {
    if (!loading && user && pendingLoaded && hasPending && !pendingChecked) {
      // Check if we already showed the modal in this session
      const sessionKey = `pending_schedules_shown_${user.id || user.email}`;
      const alreadyShown = sessionStorage.getItem(sessionKey);
      
      if (!alreadyShown) {
        // Small delay to let the page render first
        const timer = setTimeout(() => {
          setShowPendingModal(true);
          sessionStorage.setItem(sessionKey, 'true');
        }, 1500);
        return () => clearTimeout(timer);
      }
      setPendingChecked(true);
    }
  }, [loading, user, pendingLoaded, hasPending, pendingChecked]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        {showTimeout && (
          <p className="text-sm text-muted-foreground text-center px-4">
            Carregando... Se demorar muito, tente recarregar a página.
          </p>
        )}
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.nivel_acesso)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show terms acceptance modal if needed
  if (needsTermsAcceptance()) {
    return (
      <>
        <MobileLayout>{children}</MobileLayout>
        <TermsAcceptanceModal 
          open={true} 
          userName={user?.nome}
          onAccepted={() => acceptTerms()}
        />
      </>
    );
  }

  return (
    <>
      <MobileLayout>{children}</MobileLayout>
      <PendingSchedulesModal 
        open={showPendingModal} 
        onClose={() => {
          setShowPendingModal(false);
          setPendingChecked(true);
        }} 
      />
    </>
  );
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // If user is logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Otherwise, show the public news page
  return <Navigate to="/news" replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
        <div className="notranslate" translate="no">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro-publico" element={<PublicRegistrationPage />} />
          <Route path="/inscricao-publica/:projectId" element={<PublicEnrollPage />} />
          <Route path="/inscricao/:projectId" element={<PublicEnrollPage />} />
          <Route path="/musicas-publico" element={<PublicSongsPage />} />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/pessoas" element={
            <ProtectedRoute allowedRoles={["admin", "usuario", "visitante"]}>
              <PeoplePage />
            </ProtectedRoute>
          } />
          
          <Route path="/trilho" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <TrilhoReinoPage />
            </ProtectedRoute>
          } />
          
          <Route path="/pessoas/trilho" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <TrilhoReinoPage />
            </ProtectedRoute>
          } />
          
          <Route path="/conexoes" element={
            <ProtectedRoute>
              <ConnectionsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/ministerios" element={
            <ProtectedRoute allowedRoles={["admin", "usuario", "visitante"]}>
              <MinistriesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/funcoes" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <FunctionsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/musicas" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <SongsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/eventos" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <EventsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/escalas" element={
            <ProtectedRoute allowedRoles={["admin", "usuario", "visitante"]}>
              <SchedulesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/financeiro" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <FinancePage />
            </ProtectedRoute>
          } />
          
          <Route path="/financeiro/inscricoes" element={
            <ProtectedRoute allowedRoles={["admin", "usuario"]}>
              <FinanceiroInscricoesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/usuarios" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UsersPage />
            </ProtectedRoute>
          } />
          
          <Route path="/configuracoes" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <SettingsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/news-config" element={
            <ProtectedRoute>
              <NewsConfigPage />
            </ProtectedRoute>
          } />
          
          {/* Redirect old agenda-config to news-config */}
          <Route path="/agenda-config" element={<Navigate to="/news-config" replace />} />
          
          <Route path="/social" element={
            <ProtectedRoute>
              <SocialPage />
            </ProtectedRoute>
          } />
          
          <Route path="/social/inscricao/:projectId" element={
            <ProtectedRoute>
              <ProjectEnrollPage />
            </ProtectedRoute>
          } />
          
          <Route path="/whatsapp" element={
            <ProtectedRoute>
              <WhatsAppConfigPage />
            </ProtectedRoute>
          } />
          
          <Route path="/whatsapp-config" element={
            <ProtectedRoute>
              <WhatsAppConfigPage />
            </ProtectedRoute>
          } />
          
          <Route path="/frequencia" element={
            <ProtectedRoute>
              <FrequenciaPage />
            </ProtectedRoute>
          } />
          
          <Route path="/instagram-guia" element={
            <ProtectedRoute>
              <InstagramGuidePage />
            </ProtectedRoute>
          } />
          
          <Route path="/formulario-config" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <PublicFormConfigPage />
            </ProtectedRoute>
          } />
          
          <Route path="/devocional" element={
            <ProtectedRoute>
              <DevotionalPage />
            </ProtectedRoute>
          } />
          
          {/* Alias route for backwards compatibility */}
          <Route path="/devocionais" element={
            <ProtectedRoute>
              <DevotionalPage />
            </ProtectedRoute>
          } />
          
          <Route path="/devocional-config" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DevotionalConfigPage />
            </ProtectedRoute>
          } />
          
          <Route path="/meu-perfil" element={
            <ProtectedRoute>
              <MyProfilePage />
            </ProtectedRoute>
          } />
          
          <Route path="/news" element={<NewsPage />} />
          
          {/* Public routes */}
          <Route path="/inscrever/:projectId" element={<ProjectEnrollPage />} />
          <Route path="/confirmar-escala/:scheduleId/:personId" element={<ScheduleConfirmPage />} />
          <Route path="/criar-senha/:token" element={<CreatePasswordPage />} />
          <Route path="/wifi-login" element={<WiFiPortalPage />} />
          <Route path="/wifi" element={<WiFiRedirect />} />
          <Route path="/wifi-portal" element={<WiFiRedirect />} />
          <Route path="/portal" element={<WiFiRedirect />} />
          <Route path="/w" element={<WiFiRedirect />} />
          
          <Route path="/" element={<HomeRedirect />} />
        </Routes>
        </div>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
